import React, { useEffect, useState, useRef } from 'react';
import ReactPannellum from 'react-pannellum';

const Icon = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>
    {name}
  </span>
);

const Viewer360Modal = ({ isOpen, onClose, imageUrl, imageTitle }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const viewerInitializedRef = useRef(false);
  const [isOrientationActive, setIsOrientationActive] = useState(false);
  const [isOrientationInitialized, setIsOrientationInitialized] = useState(false);
  
  // Gestione chiusura modale con animazione
  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
      // Non modifichiamo lo scroll qui - lasciamo che sia il FocusPanel a gestirlo
    }, 300);
  };
  
  // Gestione isolamento del viewer quando è aperto
  useEffect(() => {
    if (isOpen) {
      // Non modifichiamo document.body.style.overflow qui
      // perché è già gestito dal FocusPanel
      
      // Aggiungi CSS inline per nascondere gli elementi non necessari
      const style = document.createElement('style');
      style.innerHTML = `
        .pnlm-panorama-info, 
        .pnlm-title-box, 
        .pnlm-author-box, 
        .pnlm-about-msg, 
        .pnlm-compass,
        .pnlm-load-button,
        .pnlm-load-box {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        if (style.parentNode) {
          document.head.removeChild(style);
        }
      };
    }
  }, [isOpen]);
  
  // Inizializza il viewer con l'orientamento del dispositivo
  const initializeViewer = () => {
    if (!window.pannellum || !window.pannellum.viewer || !window.pannellum.viewer['panorama360']) {
      // Se il viewer non è ancora inizializzato, riprova dopo un po'
      setTimeout(initializeViewer, 100);
      return;
    }
    
    // Se abbiamo già inizializzato, non facciamo nulla
    if (viewerInitializedRef.current) return;
    
    const viewer = window.pannellum.viewer['panorama360'];
    
    // Disabilitiamo completamente gli eventi touch
    const panoramaElement = document.getElementById('panorama360');
    if (panoramaElement) {
      const disableTouchInteraction = (e) => {
        // Preveniamo l'evento solo se è un evento touch
        if (e.touches) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      
      // Aggiungiamo listener per bloccare tutti gli eventi touch principali
      panoramaElement.addEventListener('touchstart', disableTouchInteraction, { passive: false });
      panoramaElement.addEventListener('touchmove', disableTouchInteraction, { passive: false });
      panoramaElement.addEventListener('touchend', disableTouchInteraction, { passive: false });
    }
    
    // Impostiamo un punto zero neutrale
    setTimeout(() => {
      try {
        // Impostiamo posizioni iniziali neutre
        viewer.setYaw(0);
        viewer.setPitch(0);
        
        // Attiviamo l'orientamento del dispositivo
        if (viewer.startOrientation) {
          viewer.startOrientation();
          // console.log('Orientamento dispositivo attivato');
        }
      } catch (e) {
        console.error('Errore nell\'inizializzazione del viewer:', e);
      }
      
      viewerInitializedRef.current = true;
    }, 300); // Un breve ritardo per evitare lo "scatto" iniziale
  };
  
  // Inizializza quando il modale è aperto
  useEffect(() => {
    if (isOpen) {
      viewerInitializedRef.current = false;
      const timer = setTimeout(initializeViewer, 300);
      
      return () => {
        clearTimeout(timer);
        viewerInitializedRef.current = false;
      };
    }
  }, [isOpen]);
  
  const handleDeviceOrientation = (event) => {
    if (!isOrientationActive) return;
    
    if (!isOrientationInitialized) {
      setIsOrientationInitialized(true);
      // console.log('Orientamento dispositivo attivato');
    }
  };
  
  if (!isOpen) return null;
  
  const modalClasses = [
    'viewer-360-modal',
    isOpen ? 'is-open' : '',
    isAnimating ? 'is-closing' : ''
  ].join(' ');
  
  // Rendering del viewer con posizionamento fixed e prevenzione dello scroll
  return (
    <div 
      className={modalClasses}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100lvh',
        zIndex: 2000,
        backgroundColor: '#000',
        overflow: 'hidden', // Nessun tipo di scroll
        transition: 'opacity 0.3s ease',
        opacity: isAnimating ? 0 : 1
      }}
    >
      {/* Pulsante di chiusura (a sinistra) */}
      <button
        onClick={handleClose}
        aria-label="Chiudi"
        className="btn bg-white d-flex align-items-center justify-content-center ratio-1x1 goback"
        style={{ 
          position: 'absolute', 
          top: '15px', 
          left: '15px',
          zIndex: 2001,
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          opacity: 0.85
        }}
      >
        <Icon name="close" 
          className="d-block mx-auto" 
          style={{ fontSize: '3rem', borderRadius: '50%' }} />
      </button>
      
      {/* Visualizzatore Pannellum - solo gyroscope */}
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <ReactPannellum
          id="panorama360"
          sceneId="panoramaScene"
          imageSource={imageUrl}
          style={{ width: '100%', height: '100%' }}
          config={{
            autoLoad: true,
            // Disabilita la rotazione automatica
            autoRotate: 0,
            // Disabilita la bussola
            compass: false,
            // Disabilita i controlli dello zoom
            showZoomCtrl: false,
            // Disabilita il controllo fullscreen
            showFullscreenCtrl: false,
            // Disabilita l'interazione con mouse/touch
            mouseZoom: false,
            // Disabilita il trascinamento
            draggable: false,
            // Disabilita i controlli da tastiera
            disableKeyboardCtrl: true,
            // Disabilita tutti i controlli dell'interfaccia
            showControls: false,
            // Attiva orientamento dispositivo per default
            orientationOnByDefault: true,
            // Imposta il campo visivo orizzontale
            hfov: 100,
            // Imposta l'angolo di inclinazione iniziale
            pitch: 0,
            // Imposta l'angolo di rotazione orizzontale iniziale
            yaw: 0,
            // Imposta il limite del campo visivo
            minHfov: 70,
            maxHfov: 120,
            // Rimuove autore, titolo, ecc.
            author: '',
            title: '',
            // Sovrascrive tutte le stringhe di testo
            strings: {
              loadButtonLabel: "",
              loadingLabel: "Caricamento...",
              bylineLabel: "",
              authorLabel: "",
              titleLabel: "",
              noPanoramaError: "",
              fileAccessError: "",
              malformedURLError: "",
              iOS8WebGLError: "",
              genericWebGLError: "",
              textureSizeError: "",
              unknownError: ""
            }
          }}
          onLoad={() => {
            // console.log('Immagine 360 caricata');
            initializeViewer();
          }}
        />
      </div>
    </div>
  );
};

export default Viewer360Modal;